import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "../../api";
import { SectionTitle, EmptyState, StatusPill, ProgressBar, stripHtml } from "../../components/Atoms";
import { Stack, Calendar, ArrowRight, BookOpen, WarningCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const TABS = [
  { key: "available", label: "Available", color: "#22D3EE" },
  { key: "applied",   label: "Applied",   color: "#F59E0B" },
  { key: "live",      label: "Live",      color: "#A3E635" },
  { key: "completed", label: "Completed", color: "#8B5CF6" },
];

export default function AmbassadorProjectsPage() {
  const [tab, setTab] = useState("available");
  const [list, setList] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await api.get(`/me/projects?tab=${tab}`);
      setList(data);
    } catch (e) {
      setLoadError(formatErr(e));
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const goDetail = (pid) => nav(`/me/projects/${pid}`);

  return (
    <div className="space-y-5">
      <SectionTitle title="Projects" subtitle="Find campaigns, drop content, get paid" />
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`amb-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap border ${tab === t.key ? "bg-white/10 text-white border-white/15" : "text-muted bg-white/5 border-white/5"}`}
            style={tab === t.key ? { boxShadow: `0 0 16px ${t.color}55` } : {}}>{t.label}</button>
        ))}
      </div>
      {loadError ? (
        <div className="glass p-8 flex flex-col items-center gap-3 text-center">
          <WarningCircle size={32} weight="fill" className="text-[#EC4899]" />
          <div className="text-white font-bold">Couldn't load projects</div>
          <div className="text-muted text-sm">{loadError}</div>
          <button onClick={load} className="btn-primary text-sm px-4 py-2 mt-2">Retry</button>
        </div>
      ) : list.length === 0 ? <EmptyState icon={Stack} title={`No ${tab} projects`} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(p => (
            <div key={p.id} onClick={() => goDetail(p.id)} className="glass p-5 hover-lift cursor-pointer" data-testid={`amb-project-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-white font-bold truncate">{p.title}</div>
                  <div className="text-xs text-muted truncate">{p.client_name}</div>
                </div>
                <StatusPill status={tab === "live" ? "Live" : tab === "completed" ? "Completed" : tab === "applied" ? "Pending" : "Active"} />
              </div>
              <div className="text-sm text-muted line-clamp-2">{stripHtml(p.description)}</div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p.task_platforms || []).map(pl => <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full">{pl}</span>)}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Total reward</div><div className="text-white font-bold">{fmtMoney(p.total_reward || 0)}</div></div>
                <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Tasks</div><div className="text-white font-bold">{p.task_count || 0}</div></div>
              </div>
              {tab === "live" && (
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-muted mb-1"><span>Progress</span><span>{p.my_approved || 0}/{p.task_count || 0}</span></div>
                  <ProgressBar value={p.my_approved || 0} max={p.task_count || 1} color="#A3E635" />
                </div>
              )}
              <div className="text-[10px] text-muted mt-2 flex items-center gap-1"><Calendar size={12} /> {fmtDate(p.start_date)} → {fmtDate(p.end_date)}</div>
              <div className="mt-3">
                {tab === "applied"
                  ? <button disabled className="btn-ghost w-full opacity-70">Waiting for admin…</button>
                  : <button onClick={(e) => { e.stopPropagation(); goDetail(p.id); }} className="btn-primary w-full flex items-center justify-center gap-1.5" data-testid={`read-more-${p.id}`}>
                      <BookOpen size={14} weight="bold" /> Read more <ArrowRight size={14} weight="bold" />
                    </button>
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
